import React, { useState, useEffect, useRef } from 'react';
import useViewport from '../../hooks/useViewport';
import { isProduction } from '../../config/environments';

const LOCAL_KEY = 'wally_chat_history';
const SESSION_KEY = 'wally_chat_open';
const SESSION_TIMEOUT_MINUTES = 60; // 1 hour

const getWallyReply = async (msg, history) => {
  try {
    // Get the auth token from localStorage
    const token = localStorage.getItem('token');
    
    // Get the correct API URL based on environment
    const getApiBaseUrl = () => {
      const envUrl = import.meta.env.VITE_API_URL;
      if (envUrl && envUrl !== '/api') {
        return envUrl.replace(/\/$/, '');
      }
      // Host-based fallback if env not set or set to '/api'
      if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (host.includes('staging')) return 'https://api.staging.mnymny.com';
        if (host.includes('mnymny.com')) return 'https://api.mnymny.com';
      }
      // Local/dev fallback (relative to same origin proxy)
      return '';
    };
    
    const apiBaseUrl = getApiBaseUrl();
    const apiUrl = `${apiBaseUrl}/api/wally-chat`;
    
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ message: msg, history })
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      console.error('[WallyChat] API error:', res.status, errorData);
      return `Wally error: ${errorData.error || `Server error (${res.status})`}`;
    }
    
    const data = await res.json();
    if (data.reply) return data.reply;
    if (data.error) return `Wally error: ${data.error}`;
    return "Sorry, I didn't get that.";
  } catch (e) {
    console.error('[WallyChat] Request error:', e);
    return "Sorry, I couldn't reach my brain (the server). Try again later!";
  }
};

// Helper function to check if user has completed onboarding
const checkOnboardingStatus = () => {
  try {
    // Check if we're currently on the onboarding page
    if (window.location.pathname === '/onboarding') {
      return false;
    }
    
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }
    
    // For now, assume if user is authenticated and not on onboarding page, they've completed it
    // In a more robust implementation, you'd check the user's onboarding status from the API
    return true;
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return false;
  }
};

export default function WallyChatModal() {
  // Hide Wally in production/master branch - not ready yet
  // Check FIRST before any other logic
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Explicitly check for production domain
    if (hostname === 'mnymny.com' || 
        hostname === 'www.mnymny.com' ||
        (hostname.includes('mnymny.com') && !hostname.includes('staging') && !hostname.includes('localhost'))) {
      return null; // Hide in production
    }
  }
  
  // Also check environment config
  if (isProduction()) {
    return null;
  }

  const { isMobile } = useViewport();
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [open, setOpen] = useState(() => {
    const wasOpen = localStorage.getItem(SESSION_KEY);
    const expiry = localStorage.getItem(SESSION_KEY + '_expiry');
    if (wasOpen && expiry && Date.now() < Number(expiry)) return true;
    return false;
  });
  const [minimized, setMinimized] = useState(false);
  const [history, setHistory] = useState(() => {
    try {
      const h = localStorage.getItem(LOCAL_KEY);
      return h ? JSON.parse(h) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Check onboarding status on mount and route changes
  useEffect(() => {
    const checkStatus = () => {
      setOnboardingCompleted(checkOnboardingStatus());
    };
    
    checkStatus();
    
    // Listen for route changes
    const handleRouteChange = () => {
      setTimeout(checkStatus, 100); // Small delay to ensure route has changed
    };
    
    window.addEventListener('popstate', handleRouteChange);
    
    // Also check periodically in case of programmatic navigation
    const interval = setInterval(checkStatus, 2000);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (open) {
      localStorage.setItem(SESSION_KEY, '1');
      localStorage.setItem(SESSION_KEY + '_expiry', Date.now() + SESSION_TIMEOUT_MINUTES * 60 * 1000);
    } else {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(SESSION_KEY + '_expiry');
    }
  }, [open]);

  useEffect(() => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(history));
    if (open && !minimized && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history, open, minimized]);

  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      const expiry = localStorage.getItem(SESSION_KEY + '_expiry');
      if (expiry && Date.now() > Number(expiry)) {
        setOpen(false);
        setHistory([]);
        localStorage.removeItem(LOCAL_KEY);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [open]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { sender: 'user', text: input, ts: Date.now() };
    setHistory((h) => [...h, userMsg]);
    setInput('');
    setLoading(true);
    const reply = await getWallyReply(userMsg.text, [...history, userMsg]);
    setHistory((h) => [...h, { sender: 'wally', text: reply, ts: Date.now() }]);
    setLoading(false);
  };

  // Hide Wally on mobile for now as requested
  if (isMobile) {
    return null;
  }

  // Hide Wally if onboarding is not completed
  if (!onboardingCompleted) {
    return null;
  }

  if (!open && !minimized) {
    return (
      <button
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg px-6 py-3 text-lg font-bold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105"
        onClick={() => setOpen(true)}
        aria-label="Open Wally Chat"
      >
        💬 Ask Wally
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {minimized && (
        <button
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg px-6 py-3 text-lg font-bold hover:from-blue-600 hover:to-purple-700 mb-2 transition-all transform hover:scale-105"
          onClick={() => setMinimized(false)}
          aria-label="Restore Wally Chat"
        >
          💬 Wally
        </button>
      )}
      {!minimized && open && (
        <div className="w-96 max-w-full bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-blue-200">
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3">
            <span className="font-bold text-lg">Wally (Project Polaris)</span>
            <div className="flex gap-3">
              <button
                className="text-white hover:text-blue-100 text-xl font-bold transition-colors"
                onClick={() => setMinimized(true)}
                aria-label="Minimize"
              >
                &#8211;
              </button>
              <button
                className="text-white hover:text-blue-100 text-xl font-bold transition-colors"
                onClick={() => { setOpen(false); setMinimized(false); setHistory([]); localStorage.removeItem(LOCAL_KEY); }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
          <div className="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-blue-50 to-purple-50" style={{ maxHeight: 400 }}>
            {history.length === 0 && (
              <div className="text-gray-600 text-center mt-8 font-medium">
                Wally is here for your financial and tax questions. No fluff, just facts. Try me!
              </div>
            )}
            {history.map((msg, i) => (
              <div key={i} className={`mb-3 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} >
                <div className={`rounded-lg px-4 py-2 max-w-xs ${
                  msg.sender === 'user' 
                    ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-900 border border-blue-200' 
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                } text-sm shadow-md`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="mb-3 flex justify-start">
                <div className="rounded-lg px-4 py-2 max-w-xs bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm shadow-md animate-pulse">
                  Wally is analyzing your finances...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <form className="flex items-center border-t border-blue-200 px-3 py-3 bg-white" onSubmit={handleSend}>
            <input
              className="flex-1 px-3 py-2 rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent mr-3 text-gray-700"
              type="text"
              placeholder="Ask Wally anything..."
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
              maxLength={500}
              autoFocus
            />
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg font-bold hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
} 